import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiArrowLeft } from 'react-icons/hi';
import LanguageSwitcher from './LanguageSwitcher';
import BotonTema from './BotonTema';

/**
 * Pinta un documento legal en el idioma activo.
 *
 * El contenido vive en `src/legal/`, no en `translation.json`, y el porqué está
 * escrito en el README de esa carpeta: son miles de palabras que necesitan
 * historial propio y revisión por versiones. El texto de la interfaz alrededor
 * —el botón de volver, la fecha— sí pasa por i18n como todo lo demás.
 *
 * El `**negrita**` se interpreta aquí en vez de traer una biblioteca de
 * Markdown entera: es lo único que hace falta y son cuatro líneas.
 */
function conNegritas(texto, clave) {
    return (
        <p key={clave} className="text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
            {texto.split(/\*\*(.+?)\*\*/g).map((trozo, i) =>
                i % 2 === 1
                    ? <strong key={i} className="font-semibold text-slate-900 dark:text-white">{trozo}</strong>
                    : trozo
            )}
        </p>
    );
}

export default function DocumentoLegal({ documento }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Volver a DONDE ESTABAS, no a un sitio fijo. Estaba puesto `to="/"`, así
    // que quien llegaba desde el formulario de registro acababa en la portada:
    // el botón prometia volver y te dejaba en otro sitio.
    //
    // React Router marca con `key === 'default'` la primera entrada del
    // historial. Si es esa, no hay «atrás» dentro de la aplicación —alguien
    // abrió el enlace directamente, o en una pestaña nueva— y entonces lo
    // honesto es ofrecer el inicio y decirlo con esas palabras.
    const hayDondeVolver = location.key !== 'default';

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-10 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Los dos controles, y aquí más que en ninguna otra pantalla.
                    `Terminos.jsx:8` elige QUÉ DOCUMENTO se pinta mirando
                    `i18n.language`: sin selector, quien llegara con la interfaz
                    en inglés leía los Términos en inglés y no tenía forma de
                    pasarlos a español. Justo en la única pantalla donde
                    entender lo que aceptas es el asunto entero. */}
                <div className="flex items-center justify-between mb-6">
                    <button
                        type="button"
                        onClick={() => (hayDondeVolver ? navigate(-1) : navigate('/'))}
                        className="inline-flex items-center gap-2 text-sm text-primary-500 hover:underline"
                    >
                        <HiArrowLeft className="w-4 h-4" />
                        {t(hayDondeVolver ? 'legal.back' : 'legal.back_home')}
                    </button>

                    <div className="flex items-center gap-1">
                        <LanguageSwitcher />
                        <BotonTema />
                    </div>
                </div>

                <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                    {documento.titulo}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
                    {t('legal.last_updated', { fecha: documento.actualizado })}
                </p>

                {documento.intro.map((p, i) => conNegritas(p, `intro-${i}`))}

                {documento.secciones.map((seccion, i) => (
                    <section key={i} className="mt-8">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">
                            {seccion.titulo}
                        </h2>
                        {seccion.parrafos.map((p, j) => conNegritas(p, `${i}-${j}`))}
                    </section>
                ))}

                <p className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-400">
                    © {new Date().getFullYear()} ShippingFULL
                </p>
            </div>
        </div>
    );
}
